import { action } from "mobx";
import { ObservableStore, IObservableStoreOpts, Callback } from "./_internal";
import { DeepPartial } from "../../../../../../common";

/**
 * Deep observable store
 */
export class ObservableDeepStore<D extends {}, N extends ObservableDeepStore<D, any> = ObservableDeepStore<D, any>> extends ObservableStore<D, N> {
	/**
	 * Constructor
	 * @param data
	 */
	public constructor(data: D, opts?: Omit<IObservableStoreOpts, "deep">) {
		super(data, { deep: true, ...(opts ?? {}) });
	}

	/**
	 * Clonse
	 * @returns
	 */
	public override clone(): N {
		return new (this.constructor as any)(this.toJS());
	}

	/**
	 * Get deep value
	 * @param key
	 */
	public getDeepValue<V = any>(key: string | string[]) {
		const path = typeof key === "string" ? key.split(".") : key;
		let o = this.data;

		while (path.length) {
			const key = path.shift();
			o = o[key];
		}

		return o as any as V;
	}

	/**
	 * Set deep value
	 * @param key
	 * @param value
	 */
	public setDeepValue(key: string | string[], value: any) {
		const path = typeof key === "string" ? key.split(".") : key;
		let o = this.data;

		while (path.length > 1) {
			const key = path.shift();
			o = o[key];
		}
		this.setNodeValue(o, path[0] as any, value);
	}

	/**
	 * Const get deep array
	 * @param key
	 */
	public getDeepArray<T = any>(key: string | string[]): T[] {
		const arr = this.getDeepValue<T[]>(key);
		if (arr !== null && !Array.isArray(arr)) throw new Error("Value is not an array at key " + key);
		return arr;
	}

	/**
	 * Get deep object
	 * @param key
	 * @returns
	 */
	public getDeepObject<T extends {} = any>(key: string | string[]): T {
		const o = this.getDeepValue<T>(key);
		if (o !== null && typeof o !== "object") throw new Error("Value is not an object at key " + key);
		return o;
	}

	/**
	 * Get deep number
	 * @param key
	 * @returns
	 */
	public getDeepNumber(key: string | string[]): number {
		const n = this.getDeepValue<number>(key);
		if (n !== null && typeof n !== "number") throw new Error("Value is not a number at key " + key);
		return n;
	}

	/**
	 * Get deep boolean
	 * @param key
	 * @returns
	 */
	public getDeepBool(key: string | string[]): boolean {
		const b = this.getDeepValue<boolean>(key);
		if (b !== null && typeof b !== "boolean") throw new Error("Value is not a booleab at key " + key);
		return b;
	}

	/**
	 * Get deep string
	 * @param key
	 * @returns
	 */
	public getDeepString(key: string | string[]): string {
		const s = this.getDeepValue<string>(key);
		if (s !== null && typeof s !== "string") throw new Error("Value is not a string at key " + key);
		return s;
	}

	/**
	 * Merge deep
	 * @param patch
	 * @param cb
	 */
	@action public mergeDeep(patch: DeepPartial<D>, cb?: Callback) {
		// Deep merge
		const deepMerge = (obj: any, src: any) => {
			if (typeof obj === "object" && obj && typeof src === "object" && src) {
				for (let key of Object.keys(src)) {
					if (Array.isArray(src[key])) {
						obj[key] = src[key];
					} else if (typeof src[key] === "object") {
						obj[key] && src[key] && deepMerge(obj[key], src[key]);
					} else obj[key] = src[key];
				}
			}
		};

		deepMerge(this.data, patch);
		cb && cb();
	}

	/**
	 * Set node value
	 * @param node
	 * @param key
	 * @param value
	 * @param cb
	 * @returns
	 */
	@action.bound public setNodeValue<V, N extends { [key in K]: V | any }, K extends keyof N>(node: N, key: K, value: V | null, cb?: Callback): V {
		this.onBeforeChange && this.onBeforeChange(this.data);
		const oldValue = node[key];

		//if (typeof oldValue === 'undefined') console.log('Undefined node key value ', key);
		(node as any)[key] = value;

		cb && cb();
		return oldValue as V | null;
	}

	/**
	 * Set nod values
	 * @param node
	 * @param patch
	 * @param value
	 * @param cb
	 */
	@action.bound public setNodeValues<V, N extends { [key in K]: V | any }, K extends keyof N>(node: N, patch: Partial<N>, cb?: Callback): void {
		if (typeof node !== "object" || !node) throw new Error("Node must be an object");
		this.onBeforeChange && this.onBeforeChange(this.data);

		for (let key in patch) {
			node[key] = patch[key];
		}
		cb && cb();
	}

	/**
	 * Similar to setNodeValues but remove unset properties
	 * @param patch
	 */
	@action public setNodeData<V, N extends { [key in K]: V | any }, K extends keyof N>(node: N, data: Partial<N>, cb?: Callback) {
		if (typeof node !== "object" || !node) throw new Error("Node must be an object");
		this.onBeforeChange && this.onBeforeChange(this.data);

		for (let key in node) {
			if (typeof data[key] === "undefined") {
				delete node[key];
			}
		}

		for (let key in data) {
			node[key] = data[key];
		}
		cb && cb();
	}

	/**
	 * Del. nodekey
	 * @param node
	 * @param patch
	 * @param cb
	 */
	@action.bound public delNodeKey<V, N extends { [key in K]: V | any }, K extends keyof N>(node: N, key: K, cb?: Callback): void {
		this.onBeforeChange && this.onBeforeChange(this.data);
		delete node[key];
		cb && cb();
	}

	/**
	 * Toggle node value
	 * @param node
	 * @param key
	 * @param cb
	 * @returns
	 */
	@action.bound public toggleNodeValue<N extends { [key in K]: boolean }, K extends keyof N>(node: N, key: K, cb?: Callback): boolean {
		this.onBeforeChange && this.onBeforeChange(this.data);

		const crtValue = node[key];
		if (crtValue !== null && typeof crtValue !== "boolean") console.log("Unexpected value when toggling key ", key);

		return this.setNodeValue(node, key, !crtValue, cb);
	}

	/**
	 * Shift node item
	 * @param node
	 * @param cb
	 * @returns
	 */
	@action public shiftNodeItem<T = any>(node: T[], cb?: (item: T) => void) {
		this.onBeforeChange && this.onBeforeChange(this.data);

		const r = node.shift();
		cb && cb(r);
		return r;
	}

	/**
	 * pushNodeItem
	 * @param node
	 * @param cb
	 * @returns
	 */
	@action public pushNodeItem<T = any>(node: T[], item: T, cb?: Callback) {
		this.onBeforeChange && this.onBeforeChange(this.data);

		const r = node.push(item);
		cb && cb();
		return r;
	}

	/**
	 * Remove node item
	 * @param node
	 * @param item
	 * @param cb
	 */
	@action public removeNodeItem<T = any>(node: T[], item: T, cb?: (length: number) => void) {
		const index = node.indexOf(item);

		if (index > -1) {
			this.onBeforeChange && this.onBeforeChange(this.data);
			node.splice(index, 1);
		}

		cb && cb(node.length);
	}

	/**
	 * Splice node
	 * @param node
	 * @param index
	 * @param delCount
	 * @param items
	 * @param cb
	 * @returns
	 */
	@action public spliceNode<T = any>(node: T[], index: number, delCount: number, items: T[], cb?: (removed?: T[]) => void) {
		this.onBeforeChange && this.onBeforeChange(this.data);
		const r = node.splice(index, delCount, ...items);
		cb && cb(r);
		return r;
	}

	/**
	 * Swap nodeitems
	 * @param node
	 * @param sIndex
	 * @param dIndex
	 * @param cb
	 * @returns
	 */
	@action public swapNodeItems<T = any>(node: T[], sIndex: number, dIndex: number, cb?: (arr?: T[]) => void) {
		this.onBeforeChange && this.onBeforeChange(this.data);

		const b = node[sIndex];
		node[sIndex] = node[dIndex];
		node[dIndex] = b;

		cb && cb(node);
		return node;
	}
}
